import React, { FC } from 'react';
import styled, { css } from 'styled-components';
import { colors } from 'src/config/colors';
import { typography } from 'src/config/typography';
import { media, widthBreakpoints } from 'src/config/breakpoints';
import { MediaQuery } from 'src/common/migration/MediaQuery';
import { textOverflow } from 'src/common/styles/typography';
import RateUpIcon from 'src/assets/icons/RateUpIcon';
import { JoinElearningButton } from 'src/public/components/elearning/JoinElearningButton';
import tmpCourseFullImage from 'src/assets/images/tmp-course-full-image-final.png';
import { Elearning } from 'src/common/models/elearning';
import { ElearningVideo } from 'src/public/components/elearning/ElearningVideo';
import { VideoContainer } from 'src/common/components/Video/VideoContainer';
import { useIsNewElearningVideo } from 'src/common/hooks/useIsNewVideo';
import { NewVideoBadge } from 'src/common/components/Video/VideoBadge';
import StarEmptyIcon from 'src/assets/icons/StarEmptyIcon';
import CategorySoftSkillsIcon from 'src/assets/icons/CategorySoftSkillsIcon';
import { localFormatNumber } from 'src/lib/localFormatNumber';

const Container = styled.div`
  ${media.w.min.px768(css`
    margin: 0 -1.5rem;
  `)}

  ${media.w.min.px992(css`
    margin: 0 -1.4375rem;
  `)}
`;

const TextContainer = styled.div`
  background: ${colors.blue};
  border-radius: 0 0 0.5rem 0.5rem;
  margin: 0 -0.75rem;
  padding: 1.25rem 0.75rem 1.25rem;

  ${media.w.min.px576(css`
    padding: 0.625rem 1.125rem 1.125rem;
  `)}

  ${media.w.min.px768(css`
    margin: 0;
    padding: 0.9375rem 1.5rem 1.25rem;
  `)}

  ${media.w.min.px992(css`
    padding: 0.9375rem 1.5625rem 1.25rem;
  `)}
`;

const Title = styled.div`
  color: ${colors.white};
  display: none;
  font-family: ${typography.fontFamilyHeaders};
  font-size: 1rem;
  line-height: 1.375rem;
  margin-bottom: 1rem;
  margin-top: auto;

  ${media.w.min.px360(css`
    display: block;
    ${textOverflow(2)}
  `)}

  ${media.w.min.px576(css`
    font-size: 1.5rem;
    line-height: 1.875rem;
    margin-bottom: 0.625rem;
  `)}

  ${media.w.min.px768(css`
    font-size: 2.3125rem;
    line-height: 2.875rem;
  `)}
`;

const StatisticItem = styled.div`
  display: flex;
  gap: 0.625rem;

  span {
    font-family: ${typography.fontFamilyHeaders};
    font-size: 1rem;
    line-height: 1.375rem;
  }
`;

const StyledStartElearningButton = styled(JoinElearningButton)`
  margin-left: auto;
`;

const StatisticContainer = styled.div`
  align-items: center;
  color: ${colors.white};
  display: flex;
  gap: 2.1875rem;
  justify-content: space-between;
  padding-right: 1.875rem;

  ${media.w.min.px576(css`
    padding-right: 0;
    width: 50%;
  `)}
`;

const StatisticWrapper = styled.div`
  align-items: center;
  display: flex;
`;

export interface ElearningRecordingPageHeaderProps {
  elearning: Elearning;
}

export const ElearningPageHeader: FC<ElearningRecordingPageHeaderProps> = ({ elearning }) => {
  const isNewElearning = useIsNewElearningVideo(elearning.createDate);

  return (
    <Container>
      <VideoContainer badges={isNewElearning && <NewVideoBadge />}>
        <ElearningVideo
          elearningId={elearning.id}
          coverPhotoUrl={elearning.coverPhoto.url ?? tmpCourseFullImage}
        />
      </VideoContainer>

      <TextContainer>
        <Title>{elearning.title}</Title>

        <StatisticWrapper>
          <StatisticContainer>
            <StatisticItem>
              <CategorySoftSkillsIcon size={18} /> <span>{elearning.participantsCount}</span>
            </StatisticItem>

            <StatisticItem>
              <RateUpIcon size={18} /> <span>{elearning.recommendationPercent}%</span>
            </StatisticItem>

            <StatisticItem>
              {elearning?.averageRating && (
                <>
                  <StarEmptyIcon size={18} />{' '}
                  <span>
                    {localFormatNumber(elearning.averageRating, {
                      maximumFractionDigits: 2,
                    })}
                  </span>
                </>
              )}
            </StatisticItem>
          </StatisticContainer>
          <MediaQuery minWidth={widthBreakpoints.px576}>
            <StyledStartElearningButton elearning={elearning} />
          </MediaQuery>
        </StatisticWrapper>
      </TextContainer>
    </Container>
  );
};
